import { create } from "zustand";
import { createSelectors } from "../createSelectors";
import { QueryKey } from "react-query";

/**
 * Outcome del workflow tras interpretar un evento del socket.
 *
 * - `isTerminal: true` → workflow terminó (éxito, parcial o falla). Se
 *   resuelve del store, se dismissea el indicador y el toast persistente.
 * - `isTerminal: false` → workflow sigue en curso (ej: transferencia de
 *   stock que pasó de `created` a `approved` pero todavía no terminó).
 *   Se invalidan queries y se muestra el toast, pero el workflow sigue
 *   pendiente.
 */
export interface EventOutcome {
  isTerminal: boolean;
  toast?: {
    // `error`, no `destructive`: es la variante estándar para toasts de error
    // (ver CLAUDE.md → Toast System). `destructive` queda para Button/Badge/Alert.
    variant: "success" | "warning" | "error" | "info";
    title: string;
    description?: string;
    duration?: number;
  };
  /**
   * Override de queries a invalidar para este evento puntual.
   * Si se omite, usa las `queryKeysToInvalidate` del workflow.
   */
  queryKeysToInvalidate?: QueryKey[];
}

/**
 * Un workflow async en curso del backend (típicamente un workflow de Temporal).
 *
 * El frontend registra el workflow al disparar el request, escucha el evento
 * del socket (`socketEventType`) y, al recibir un evento que matchea, ejecuta
 * `interpretEvent(payload)` para decidir qué hacer (toast, invalidate, si es
 * terminal).
 *
 * Match: un evento matchea cuando `notification.type === socketEventType` Y
 * cualquier valor de `correlationIds` aparece como valor en `payload`. Si
 * `correlationIds` está vacío y `fifoMatch === true`, matchea el primer
 * workflow pendiente de ese type (útil para CSVs que no devuelven ID).
 *
 * `entityKeys` permite que componentes como `<PendingIndicator />` sepan si
 * hay un workflow pendiente para una entidad específica (ej: una variante
 * o un proveedor).
 */
export interface PendingWorkflow {
  /**
   * Identificador interno único en el store. NO se compara contra el
   * payload del evento. Suele ser el primer correlationId o un random.
   */
  id: string;

  /**
   * Tipo(s) de evento del socket que matchea(n) con este workflow.
   * Pasar un array cuando un workflow puede terminar con eventos distintos
   * (ej: `inventory.csv.processed` o `inventory.csv.failed`).
   */
  socketEventType: string | string[];

  /**
   * Valores que pueden aparecer en el payload del evento para matchear.
   * Ej: `[importId]`, `[publishId]`, `[workflowId, bulkId]`.
   * Si está vacío, considerar `fifoMatch`.
   */
  correlationIds: string[];

  /**
   * Si no hay correlationIds, ¿matchear el primer workflow pendiente de
   * este `socketEventType`? Útil para CSV uploads donde el evento no
   * trae correlation ID (`inventory.csv.processed`).
   */
  fifoMatch?: boolean;

  /**
   * Queries a invalidar por default cuando un evento matchea
   * (a menos que `interpretEvent` devuelva sus propias queries).
   */
  queryKeysToInvalidate: QueryKey[];

  /**
   * Entidades afectadas (para `<PendingIndicator entityKey={...} />`).
   * Ej: ["product:abc", "supplier:xyz"].
   */
  entityKeys: string[];

  /**
   * Interpreta el payload del evento y decide qué hacer.
   * Si se omite, se usa una interpretación default basada en `payload.status`
   * con valores `completed | partial | failed` (caso pricing).
   */
  interpretEvent?: (
    payload: Record<string, any>,
    eventType: string
  ) => EventOutcome;

  startedAt: number;
  timeoutId?: ReturnType<typeof setTimeout>;
  /**
   * Intervalo del sondeo al servidor. Es el que cierra el workflow cuando el
   * evento del socket no llega — ver `useWorkflowMutation`.
   */
  pollIntervalId?: ReturnType<typeof setInterval>;
  /**
   * Id del toast "Actualización en curso" persistente. Se dismissea cuando
   * el workflow se vuelve terminal o el timeout fallback se dispara.
   */
  acceptedToastId?: string;
}

interface PendingWorkflowsState {
  pending: Record<string, PendingWorkflow>;
  register: (workflow: PendingWorkflow) => void;
  resolve: (id: string) => void;
}

const store = create<PendingWorkflowsState>((set, get) => ({
  pending: {},

  register: (workflow) =>
    set((state) => ({
      pending: { ...state.pending, [workflow.id]: workflow },
    })),

  resolve: (id) => {
    const workflow = get().pending[id];
    if (workflow?.timeoutId) clearTimeout(workflow.timeoutId);
    if (workflow?.pollIntervalId) clearInterval(workflow.pollIntervalId);
    set((state) => {
      const next = { ...state.pending };
      delete next[id];
      return { pending: next };
    });
  },
}));

export const usePendingWorkflowsStore = createSelectors(store);

/**
 * ¿Hay un workflow pendiente que afecte a esta entidad?
 *
 * Acepta **varias** claves y responde con un OR: útil cuando una fila de la UI
 * representa a más de una entidad del workflow. Un precio existe por proveedor,
 * así que una variante con dos proveedores tiene dos `price:` — y el usuario
 * quiere ver **un** indicador en la variante, no uno por proveedor.
 */
export const useHasPendingWorkflow = (
  entityKey: string | string[]
): boolean => {
  const keys = Array.isArray(entityKey) ? entityKey : [entityKey];
  return usePendingWorkflowsStore((state) =>
    Object.values(state.pending).some((wf) =>
      keys.some((key) => wf.entityKeys.includes(key))
    )
  );
};

/**
 * Interpretación default: usa `payload.status` con valores
 * `completed | partial | failed`. Aplica a pricing y a otros workflows
 * con la misma forma de evento.
 */
export const defaultInterpretEvent = (
  payload: Record<string, any>
): EventOutcome => {
  const status = payload.status as
    | "completed"
    | "partial"
    | "failed"
    | undefined;
  const successCount = payload.successCount ?? 0;
  const failedCount = payload.failedCount ?? 0;
  const totalCount =
    payload.totalPrices ?? payload.totalProducts ?? payload.total ?? 0;

  if (status === "failed") {
    return {
      isTerminal: true,
      toast: {
        variant: "error",
        title: "La operación falló",
        description:
          "No se pudo completar la actualización. Por favor, intentá nuevamente.",
        duration: 6000,
      },
    };
  }

  if (status === "partial") {
    return {
      isTerminal: true,
      toast: {
        variant: "warning",
        title: "Actualización parcial",
        description: `Se aplicaron ${successCount} de ${totalCount} cambios. ${failedCount} fallaron.`,
        duration: 7000,
      },
    };
  }

  // completed (o sin status — asumimos éxito)
  return {
    isTerminal: true,
    toast: {
      variant: "success",
      title: "Actualización completada",
      description: "Los cambios fueron aplicados correctamente.",
      duration: 3500,
    },
  };
};

/**
 * Busca dentro del payload (incluyendo objetos anidados) si alguno de los
 * valores está presente. Útil para matchear correlation IDs sin saber el
 * nombre exacto del campo en el payload.
 */
export const payloadContainsAny = (
  payload: Record<string, any>,
  values: string[]
): boolean => {
  if (values.length === 0) return false;
  const valueSet = new Set(values);

  const walk = (node: any): boolean => {
    if (node == null) return false;
    if (typeof node === "string" && valueSet.has(node)) return true;
    if (Array.isArray(node)) return node.some(walk);
    if (typeof node === "object") return Object.values(node).some(walk);
    return false;
  };

  return walk(payload);
};
